
import { Component, computed, effect, inject, OnDestroy, OnInit, signal, WritableSignal } from '@angular/core';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { MatExpansionModule } from '@angular/material/expansion';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatSelectModule } from '@angular/material/select';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import { MatSliderModule } from '@angular/material/slider';
import { MatTooltipModule } from '@angular/material/tooltip';
import { Image as ImageLayer, Tile as TileLayer } from 'ol/layer';
import { ImageWMS, TileWMS } from 'ol/source';
import BaseLayer from 'ol/layer/Base';
import { filter, take } from 'rxjs';
import { GenServiceType } from '../../../generated/serverModels/GenServiceType';
import { AppService } from '../../../services/app.service';
import { FilterRow, GlobalFilterService } from '../../../services/global-filter.service';
import { LayerService } from '../../../services/layer.service';
import { MapService } from '../../../services/map.service';
import { BaseUILayer } from '../../classes/layers/base-ui-layer';
import { SingleSelectFilterComponent } from '../../filters/single-select-filter/single-select-filter.component';
import { TextFilterComponent } from '../../filters/text-filter/text-filter.component';
import { LayerHeaderComponent } from '../layer-header/layer-header.component';

@Component({
  selector: 'tgrid-map-layer',
  imports: [MatExpansionModule, MatSlideToggleModule, MatTooltipModule, MatSliderModule, MatFormFieldModule, MatSelectModule, ReactiveFormsModule, SingleSelectFilterComponent, TextFilterComponent, LayerHeaderComponent],
  standalone: true,
  templateUrl: './map-layer.component.html',
  styleUrl: './map-layer.component.scss',
})
export class MapLayerComponent extends BaseUILayer<BaseLayer> implements OnInit, OnDestroy {
  public globalFilterService = inject(GlobalFilterService);
  public appService = inject(AppService);
  private layerService = inject(LayerService);

  protected selectOptions: WritableSignal<FilterRow[]> = signal([]);
  protected attributeControl: FormControl = new FormControl();

  private identifyChange = toSignal(this.mapService.activeIdentifyLayers);
  protected isIdentifyActive = computed(() => {
    return (this.identifyChange()?.findIndex(ml => ml.uiHandle === this.mapLayer().uiHandle) !== -1);
  });


  // Filter properties that will need to be moved
  private filterMap = new Map<string, string>();
  protected applicableFilters: {abbreviation: boolean; state: boolean; date: boolean} = {
    abbreviation: false,
    state: false,
    date: true,
  };

  constructor() {
    super();

    effect(() => {
      this.appService.selectedStates();
      this.appService.selectedYears();
      if (this.checked) {
        this.toggleLayer(true);
      }
    });

    this.attributeControl.valueChanges.pipe(
      filter((value) => !!value && this.checked),
      takeUntilDestroyed(),
    )
      .subscribe(value => {
        const source: TileWMS | ImageWMS | null = (this.mapLayer().renderedLayer as TileLayer<TileWMS> | ImageLayer<ImageWMS>).getSource();
        this.mapLayer().selectedAttribute = value;
        // source?.updateParams({'env': `field:${value.dbColumn}`})
        if (value.apiLegend) {
          let sldUrl = `${location.origin.replace('localhost', 'host.docker.internal')}/api/maplayer/sld/${source?.getParams()?.LAYERS.split(':').pop()}/${value.legendName}/${value.dbColumn}`;
          if (value.type) {
            sldUrl += `?type=${value.type}`;
          }
          source?.updateParams({ 'sld': sldUrl });
          source?.updateParams({ 'STYLES': value.legendName });
          this.mapLayer().legend.imageUrl.set(`${this.mapLayer().legend.imageUrl()?.replace(new RegExp('&sld=[^&]*'), '')}&sld=${encodeURIComponent(sldUrl)}`);
        }
      });

    effect(() => {
      if (this.selectOptions().length > 0) {
        this.attributeControl.setValue(this.selectOptions()[0]);
      }
    });
  }


  ngOnInit(): void {

    switch (this.mapLayer().uiHandle) {
      case 'cejstBoolean':
        this.selectOptions.set(this.globalFilterService.getCejstBooleanProperties());
        break;
      case 'cejstFundamentals':
        this.selectOptions.set(this.globalFilterService.getCejstFundamentalsProperties());
        break;
      case 'cejstFundamentalsEsa':
        this.selectOptions.set(this.globalFilterService.getCejstFundamentalsProperties());
        break;
      case 'cejstPercent':
        this.selectOptions.set(this.globalFilterService.getCejstPercentProperties());
        break;
      case 'cejstPercentile':
        this.selectOptions.set(this.globalFilterService.getCejstPercentileProperties());
        break;
      case 'cejstCountySummary':
        this.selectOptions.set(this.globalFilterService.getCejstCountySummaryProperties());
        break;
      case 'eiaAdvancedMeter':
        this.selectOptions.set(this.globalFilterService.getEiaMeterProperties());
        break;
      case 'eiaReliability':
        this.selectOptions.set(this.globalFilterService.getEiaReliabilityProperties());
        break;
      case 'eiaDistributionSystems':
        this.selectOptions.set(this.globalFilterService.getEiaDistributionSystemsProperties());
        break;
      case 'eiaReliabilityFrequency':
        this.selectOptions.set(this.globalFilterService.getEiaReliabilityFrequencyProperties());
        break;
      case 'fsimFireRisk':
        this.selectOptions.set(this.globalFilterService.getBurnProbabilityProperties());
        break;
      case 'stateFireRisk':
        this.selectOptions.set(this.globalFilterService.getStateBurnIndexProperties());
        break;
      case 'icfSubstations':
        this.selectOptions.set(this.globalFilterService.getIcfSubstationProperties());
        break;
      case 'icfPowerPlants':
        this.selectOptions.set(this.globalFilterService.getIcfPowerplantProperties());
        break;
      case 'icfTransmissionLines':
        this.selectOptions.set(this.globalFilterService.getIcfTransmissionLinesProperties());
        break;
    }


    this.applicableFilters = {
      abbreviation: ['tgrid:fire_probability_mv', 'tgrid:electric_service_areas_vw', 'tgrid:county_summary', 'tgrid:cejst', 'tgrid:fundamentals_vw', 'tgrid:fundamentals_esa_vw', 'tgrid:distribution_systems', 'tgrid:eia_reliability', 'tgrid:advanced_meter', 'tgrid:county_vw', 'tgrid:state'].includes(this.mapLayer().serviceLayer) ||
        ['countyServiceProvider', 'fsimFireRisk', 'stateFireRisk', 'icfPowerPlants', 'icfSubstations'].includes(this.mapLayer().uiHandle),
      state: ['eaglei:substations', 'eaglei:power_plants'].includes(this.mapLayer().serviceLayer),
      date: ['eiaDistributionSystems', 'eiaAdvancedMeter', 'eiaReliabilityFrequency', 'eiaReliability'].includes(this.mapLayer().uiHandle),
    };

    // TODO: Removed this layer, this is just for demo purposes.
    // if(AuthService.authenticatedUser.getValue()!.isAdmin()) {
    //   this.applicableFilters.abbreviation = false;
    //   this.applicableFilters.state = false;
    // }

  }

  override onDestroy(): void {
  }

  public override toggleLayer(checked: boolean) {
    this.checked = checked;
    if (this.checked) {
      if (!this.mapLayer().renderedLayer) {
        switch (this.mapLayer().serviceType) {
          case GenServiceType.WMS:
            this.mapLayer().renderedLayer = this.mapService.createTileSource(this.mapLayer());
            this.attributeControl.updateValueAndValidity();
            break;
        }
      }

      (this.mapLayer().renderedLayer as TileLayer<TileWMS> | ImageLayer<ImageWMS>).getSource()?.updateParams({
        CQL_FILTER: this.getCqlFilter(),
      });

      if (this.mapLayer().renderedLayer && this.mapService.isLayerOnMap(this.mapLayer().renderedLayer)) {
        return;
      }


      // TODO look at moving the to a parent source class
      MapService.getMapRef()!.addLayer(this.mapLayer().renderedLayer);
    } else {
      if (this.isIdentifyActive()) {
        this.mapService.activeIdentifyLayers.next([]);
      }

      MapService.getMapRef()!.removeLayer(this.mapLayer().renderedLayer);

    }
  }

  public override changeOpacity(opacity: number) {
    this.opacity = opacity;
    (this.mapLayer().renderedLayer as TileLayer<TileWMS> | ImageLayer<ImageWMS>).setOpacity(this.opacity / 100);
  }

  public toggleIdentify() {
    let activeIdentifyLayers = this.mapService.activeIdentifyLayers.getValue();
    const index = activeIdentifyLayers.findIndex(ml => ml.uiHandle === this.mapLayer().uiHandle);

    if (index === -1) {
      activeIdentifyLayers = [this.mapLayer()];
    } else {
      activeIdentifyLayers.splice(index, 1);
    }

    this.mapService.activeIdentifyLayers.next([...activeIdentifyLayers]);
  }

  private getCqlFilter(): string {
    const cqlFilter: string[] = [];

    if (this.applicableFilters.abbreviation) {
      const states = this.appService.selectedStates().map(state => state.abbreviation);
      cqlFilter.push(`abbreviation IN ('${states.join("','")}')`);
    }

    if (this.applicableFilters.state) {
      const states = this.appService.selectedStates().map(state => state.abbreviation);
      cqlFilter.push(`state IN ('${states.join("','")}')`);
    }

    if (this.applicableFilters.date) {
      const years = this.appService.selectedYears();
      cqlFilter.push(`data_year IN (${years.join(',')})`);
    }


    cqlFilter.push(...Array.from(this.filterMap.values()));


    const finalCql = this.mapLayer().serviceLayer.split(',')
      .map(() => {
        return cqlFilter.join(' AND ');
      })
      .join(';');

    if (finalCql.length === 0) return '1=1';
    return finalCql;
  }

  public exportDataAsCsv(event: MouseEvent): void {
    event.stopPropagation();

    this.layerService.getWfsJsonData(this.mapLayer().serviceUrl, this.mapLayer().serviceLayer, this.getCqlFilter())
      .pipe(take(1))
      .subscribe(data => {
        this.mapLayer().downloadFeaturesAsCsv(data as {properties: Record<string, unknown>}[]);
      });
  }


  // this needs to be updated to a filetr type
  updateCqlFilter(type: string, property: string, value: string | string[]) {
    const key = `${type}_${property}`.toLowerCase();
    let mapValue: string | undefined;
    switch (type) {
      case 'SELECT':
      case 'MULTISELECT':
        if (Array.isArray(value)) {
          const predicate = value.length === 0 ? '\'TASTI-GRID\'' : value.map(v => `'${v}'`).join();
          mapValue = `${property} IN (${predicate})`;
        } else {
          mapValue = `${property} = '${value}'`;
        }
        break;

      case 'TEXT_SEARCH':
        mapValue = `strToLowerCase(${property}) like '%${(value as string).toLowerCase()}%'`;
        break;
    }

    if (mapValue) {
      this.filterMap.set(key, mapValue);
      this.toggleLayer(true);
    }


  }

  handleHeaderAction(event: string) {
    if (event !== 'identify') return;
    this.toggleIdentify();
  }
}
